// ─────────────────────────────────────────────────────────────────────────────
// agentLane.ts — the per-agent lane (frontier lanes, Phase 4): which model an
// agent runs on. automatic · local · api · sub (Sub = Claude via the Max credit,
// or GPT via the ChatGPT Pro login).
//
// THE SERVER'S TRUTH, PER AGENT. Every read names the agent it asked about and a
// body that answers for another agent is discarded — the chat shows its own
// agent's lane or nothing, never a neighbour's (the model pill's leak, not
// repeated). The server keys a cross-domain agent by its home domain, so the
// answer's domain may differ from the chat's; the agent never does.
//
// No axios here: the chat surfaces import this module, and axios 1.x is ESM that
// the CRA jest setup cannot load without a per-test mock. fetch + the shell's
// Bearer is the idiom api/agentLoop.ts already uses.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';
import {
  AGENT_LANE,
  AGENT_LANE_ELIGIBLE,
  agentLaneModels,
  laneImage,
} from '../data/dashboard/endpoints';

export type Lane = 'automatic' | 'local' | 'api' | 'sub';
export type SubProvider = 'claude' | 'gpt';

export const LANES: readonly Lane[] = ['automatic', 'local', 'api', 'sub'];
export const SUB_PROVIDERS: readonly SubProvider[] = ['claude', 'gpt'];

export interface LaneState {
  /** The agent's home domain, as the server keys it (upper case). */
  domain: string;
  /** Lower case. */
  agent: string;
  lane: Lane;
  /** Sub only. */
  provider: string;
  /** Sub only. */
  model: string;
}

export interface LaneModel {
  id: string;
  displayName: string;
}

export interface EligibleAgent {
  domain: string;
  agent: string;
}

// endpoints.js paths are rooted at /api; apiBase() is where /api is for this page.
function url(path: string): string {
  return apiBase() + path.replace(/^\/api(?=\/)/, '');
}

function headers(json = false): Record<string, string> {
  const token = authToken();
  const h: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/**
 * A wire body → the lane it states, or null. NEVER throws. A body for another
 * agent, or naming a lane this surface does not know, is not a lane.
 */
export function normalizeLane(d: any, requestedAgent: string): LaneState | null {
  if (!d || typeof d !== 'object' || d.success === false) return null;
  const agent = typeof d.agent === 'string' ? d.agent.trim().toLowerCase() : '';
  if (!agent || agent !== String(requestedAgent || '').trim().toLowerCase()) return null;
  if (LANES.indexOf(d.lane) < 0) return null;
  const sub = d.lane === 'sub';
  return {
    domain: String(d.domain || '').trim().toUpperCase(),
    agent,
    lane: d.lane as Lane,
    provider: sub ? String(d.provider || '') : '',
    model: sub ? String(d.model || '') : '',
  };
}

// The last lane read for each chat (its own domain + agent): the client-side
// limits that follow the lane read it (see laneTimeout).
const known = new Map<string, Lane>();

export function chatKey(domain: string | null | undefined, agent: string | null | undefined): string {
  return `${String(domain || '').trim().toUpperCase()}:${String(agent || '').trim().toLowerCase()}`;
}

function remember(domain: string, agent: string, state: LaneState | null) {
  if (state) known.set(chatKey(domain, agent), state.lane);
  else known.delete(chatKey(domain, agent));
}

/**
 * A client-side request limit, as the server applies its own (R29, 3B-19/20):
 * none on a Sub-lane turn; `today` on every other lane, and when the lane has
 * not been read (never guessed).
 */
export function laneTimeout(domain: string | null | undefined, agent: string | null | undefined,
                            today: number): number {
  return known.get(chatKey(domain, agent)) === 'sub' ? 0 : today;
}

/** The stored lane for this chat's agent, or null (unread / refused / another agent's). */
export async function getLane(domain: string, agent: string): Promise<LaneState | null> {
  let state: LaneState | null = null;
  try {
    const q = `?domain=${encodeURIComponent(domain)}&agent=${encodeURIComponent(agent)}`;
    const res = await fetch(url(AGENT_LANE) + q, { headers: headers() });
    if (res.ok) state = normalizeLane(await res.json(), agent);
  } catch {
    state = null;
  }
  remember(domain, agent, state);
  return state;
}

/**
 * Save a lane. Sub is saved only with a provider AND a model — refused here,
 * before any request, as the server would refuse it. Returns what the server
 * now STORES (its POST answers with the stored state), or the server's reason.
 */
export async function setLane(
  domain: string,
  agent: string,
  lane: Lane,
  provider = '',
  model = '',
): Promise<{ state: LaneState | null; error: string | null }> {
  if (LANES.indexOf(lane) < 0) return { state: null, error: `unknown lane ${lane}` };
  if (lane === 'sub' && (!provider || !model)) {
    return { state: null, error: 'Sub is saved with a model — pick one' };
  }
  try {
    const res = await fetch(url(AGENT_LANE), {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify({
        domain,
        agent,
        lane,
        provider: lane === 'sub' ? provider : '',
        model: lane === 'sub' ? model : '',
      }),
    });
    const body = await res.json().catch(() => null);
    const state = res.ok ? normalizeLane(body, agent) : null;
    if (state) remember(domain, agent, state);
    return {
      state,
      error: state ? null : String((body && body.error) || `the lane was not saved (HTTP ${res.status})`),
    };
  } catch (e: any) {
    return { state: null, error: `the lane was not saved (${e?.message || 'no answer'})` };
  }
}

/** Every agent that may carry a lane, as the server derives it from the roster; null if unread. */
export async function getEligible(): Promise<EligibleAgent[] | null> {
  try {
    const res = await fetch(url(AGENT_LANE_ELIGIBLE), { headers: headers() });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || d.success === false || !Array.isArray(d.agents)) return null;
    return d.agents
      .filter((a: any) => a && typeof a.domain === 'string' && typeof a.agent === 'string')
      .map((a: any) => ({ domain: a.domain.trim().toUpperCase(), agent: a.agent.trim().toLowerCase() }));
  } catch {
    return null;
  }
}

/** True when the server's answer for this chat names an eligible agent. */
export function isEligible(rows: EligibleAgent[] | null, state: LaneState | null): boolean {
  if (!rows || !state) return false;
  return rows.some((r) => r.domain === state.domain && r.agent === state.agent);
}

/** The Sub model menu for one provider; null if unread. */
export async function getModels(provider: SubProvider): Promise<LaneModel[] | null> {
  try {
    const res = await fetch(url(agentLaneModels(provider)), { headers: headers() });
    if (!res.ok) return null;
    const d = await res.json();
    if (!d || d.success === false || !Array.isArray(d.models)) return null;
    return d.models
      .filter((m: any) => m && typeof m.id === 'string' && m.id)
      .map((m: any) => ({ id: m.id, displayName: String(m.display_name || m.id) }));
  } catch {
    return null;
  }
}

/** One stored image, fetched WITH the Bearer (the route requires auth); null if unread. */
export async function fetchLaneImage(id: string): Promise<Blob | null> {
  try {
    const res = await fetch(url(laneImage(id)), { headers: headers() });
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}
